import Popper from '@mui/material/Popper';
import styles from './Dropdown.module.css';
import Options from './Options/Options';
import AiSuggestionsSection from './AiSuggestionsSection/AiSuggestionsSection';
import OcrValuesSection from './OcrValuesSection/OcrValuesSection';
import { useDropdownBehavior } from 'hooks/transaction/form/useDropdownBehavior';

function Dropdown({ dropdownOpened, noOptionsText, isOptionsLoadingInProgress, optionsLoadingText, isTruncated, maxRenderedOptions, ...props }) {
    const { filteredOptions, freeSolo, aiSuggestions, ocrGroups, getInputValue, anchorRef } = props;
    // A verified field lists its OCR values in place of the AI suggestions; `aiSuggestions` then holds the pickable ones.
    const hasOcrValues = !!ocrGroups?.length;

    const {
        dropdownRef,
        selectedSuggestionIndex,
        selectedOptionIndex,
        activeAiSuggestionIndex,
        hasAiSuggestions,
        hasOptions,
        handleSetSelectedOptionIndex,
        handleAiSuggestionSelect,
        handleOptionChange,
    } = useDropdownBehavior({
        dropdownOpened,
        filteredOptions,
        aiSuggestions,
        ...props,
    });

    // Free typing with nothing to offer (e.g. a field verified by OCR before its first read): no empty box under it.
    if (!hasOcrValues && !hasAiSuggestions && !hasOptions && freeSolo && !isOptionsLoadingInProgress) return null;

    const dropdownContent = (
        <div
            className={`${styles['dropdown']}${hasOcrValues ? ` ${styles['dropdown--ocr']}` : ''} notranslate`}
            translate="no"
            data-gramm="false"
            data-gramm_editor="false"
            data-enable-grammarly="false"
            data-no-translate="true"
            ref={dropdownRef}
        >
            {dropdownOpened && hasOcrValues && (
                <OcrValuesSection
                    groups={ocrGroups}
                    onSelect={handleAiSuggestionSelect}
                    selectedIndex={selectedSuggestionIndex}
                    activeIndex={activeAiSuggestionIndex}
                />
            )}
            {dropdownOpened && !hasOcrValues && hasAiSuggestions && (
                <AiSuggestionsSection
                    aiSuggestions={aiSuggestions}
                    onSelect={handleAiSuggestionSelect}
                    getInputValue={getInputValue}
                    selectedIndex={selectedSuggestionIndex}
                    activeIndex={activeAiSuggestionIndex}
                />
            )}
            {dropdownOpened && hasOptions && isTruncated && (
                <p className={styles['results-hint']} role="status" aria-live="polite">
                    Showing first {maxRenderedOptions} results. Keep typing to narrow the list.
                </p>
            )}
            {dropdownOpened && hasOptions && (
                <Options
                    {...props}
                    onChange={handleOptionChange}
                    selectedOptionIndex={selectedOptionIndex}
                    setSelectedOptionIndex={handleSetSelectedOptionIndex}
                />
            )}
            {dropdownOpened && !hasOptions && !hasAiSuggestions && !hasOcrValues && !freeSolo && (
                <p className={styles['no-option']}>{isOptionsLoadingInProgress ? optionsLoadingText : noOptionsText}</p>
            )}
        </div>
    );

    if (!anchorRef?.current) {
        return dropdownContent;
    }

    return (
        <Popper
            open={dropdownOpened}
            anchorEl={anchorRef.current}
            placement="bottom-start"
            disablePortal={false}
            style={{ zIndex: 9999, minWidth: anchorRef.current?.clientWidth || '100px', maxWidth: '320px' }}
            modifiers={[
                {
                    name: 'offset',
                    options: {
                        offset: [0, 2],
                    },
                },
                {
                    name: 'preventOverflow',
                    options: {
                        padding: 8,
                    },
                },
            ]}
        >
            {dropdownContent}
        </Popper>
    );
}

export default Dropdown;
